import React, {FormEventHandler, forwardRef, ReactNode, useEffect} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'

import MobxCheckbox from 'components/elements/fields/MobxCheckbox'
import MobxInputQuantity from 'components/elements/fields/MobxInputQuantity/MobxInputQuantity'
import MobxSelect from 'components/elements/fields/MobxSelect'
import MobxTextInput from 'components/elements/fields/MobxTextInput'
import {Col, Row} from 'components/elements/grid'
import {maritalOptions} from 'models/PayStub/PayStub.configs'
import usStates from 'src/configs/usStates'
import {posthogTrack} from 'src/utils'
import {useAuthStore, usePayStubStore} from 'src/utils/contexts/RootStore.context'

import s from './FieldsPayStubEmployeeInfoNew.module.scss'

const cx = cn.bind(s)

interface IFieldsPayStubEmployeeInfoNewProps {
	className?: string
	children?: ReactNode | string
	onChange?: FormEventHandler<HTMLDivElement>
}

const FieldsPayStubEmployeeInfoNew = forwardRef<HTMLDivElement, IFieldsPayStubEmployeeInfoNewProps>(
	({className, children, ...props}, ref) => {
		const payStubStore = usePayStubStore()
		const {
			employeeInfo: {isDirectDeposit},
			setEmployeeInfo,
		} = payStubStore
		const authStore = useAuthStore()

		useEffect(() => {
			if (!authStore.hasActiveSubscription) {
				posthogTrack(`[PAYSTUB unsubscribed] Step 4 started`)
			}
		}, [authStore.hasActiveSubscription])

		useEffect(() => {
			posthogTrack(`[PAYSTUB] Step 4 started`)
		}, [])

		return (
			<div className={cx('employee', className)} data-testid="employee-info" {...props} ref={ref}>
				{children}
				<div className={cx('employee--fields')}>
					<Row className={cx('employee__row')}>
						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Employee name'}
								placeholder="Your Full Name"
								store={payStubStore.employeeInfo}
								name="employeeName"
								required
								variant="new"
								size="large"
							/>
						</Col>
						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Employee telephone number'}
								placeholder="123-456-7890"
								store={payStubStore.employeeInfo}
								name="employeeTelephone"
								variant="new"
								size="large"
							/>
						</Col>
					</Row>
					<Row className={cx('employee__row')}>
						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Employee social'}
								placeholder="Last 4 Digits"
								store={payStubStore.employeeInfo}
								onChange={(e) => setEmployeeInfo(e.target.value)}
								name="employeeSocial"
								variant="new"
								size="large"
							/>
						</Col>
						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Employee id'}
								placeholder="1234"
								store={payStubStore.employeeInfo}
								name="employeeId"
								variant="new"
								size="large"
							/>
						</Col>
					</Row>
					<MobxTextInput
						wrapperClassName={cx('field')}
						label={'Employee address 1'}
						placeholder="Your Full Address"
						store={payStubStore.employeeInfo}
						name="streetAddress1"
						required
						variant="new"
						size="large"
					/>
					<MobxTextInput
						wrapperClassName={cx('field')}
						label={'employee address 2'}
						placeholder="Suite 1234"
						store={payStubStore.employeeInfo}
						name="streetAddress2"
						variant="new"
						size="large"
					/>
					<MobxSelect
						name="state"
						placeholder="Choose state"
						label="State"
						store={payStubStore.employeeInfo}
						wrapperClassName={cx('field')}
						data-testid="state"
						required
						onFocus={payStubStore.pauseCalculation}
						onBlurCapture={payStubStore.resumeCalculation}
						onChange={(e) => {
							payStubStore.employeeInfo.state = e.target.value
						}}
						variant="new"
						size="large"
						options={usStates}
					/>
					<Row className={cx('employee__row')}>
						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'City'}
								placeholder="Your City"
								store={payStubStore.employeeInfo}
								name="city"
								required
								variant="new"
								size="large"
							/>
						</Col>

						<Col md={6}>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={'Zip code'}
								placeholder="12345"
								store={payStubStore.employeeInfo}
								name="zipCode"
								required
								variant="new"
								size="large"
								maxLength={5}
							/>
						</Col>
					</Row>
					<Row className={cx('employee__row')}>
						<Col md={6}>
							<MobxSelect
								name="maritalStatus"
								label="Marital status"
								store={payStubStore.employeeInfo}
								options={maritalOptions}
								onFocus={payStubStore.pauseCalculation}
								onBlurCapture={payStubStore.resumeCalculation}
								onChange={(e) => {
									payStubStore.employeeInfo.maritalStatus = e.target
										.value as calculator.TFederalFilingStatus
								}}
								wrapperClassName={cx('field')}
								variant="new"
								size="large"
							/>
						</Col>
						<Col md={6}>
							{/* TODO: refactor paystubs store values for quantity inputs to be numbers instead of strings */}
							<MobxInputQuantity
								variant="new"
								fullWidth
								wrapperClassName={cx('field')}
								label="Dependents amount"
								name="dependentsAmount"
								store={payStubStore.employeeInfo}
								max={99}
								min={0}
							/>
						</Col>
					</Row>
					<div className={cx('deposit')}>
						<div>
							<div className={cx('deposit__wrapper')}>
								<MobxCheckbox
									classNameWrapper={cx('deposit__check')}
									name="isDirectDeposit"
									label="Direct deposit"
									store={payStubStore.employeeInfo}
									value={payStubStore?.employeeInfo.isDirectDeposit === true}
								/>
							</div>
						</div>
						{isDirectDeposit && (
							<MobxTextInput
								wrapperClassName={cx('digital-field', 'field')}
								variant="new"
								label={'Last 4 Digits of Bank Checking Account'}
								placeholder="XXXX"
								store={payStubStore.employeeInfo}
								onChange={(e) => {
									const value = e.target.value
									payStubStore.employeeInfo.last4Digits = value.slice(0, 4)
								}}
								name="last4Digits"
								type="number"
								maxLength={4}
								min="1"
								max="9999"
							/>
						)}
					</div>
				</div>
			</div>
		)
	},
)

FieldsPayStubEmployeeInfoNew.displayName = 'FieldsPayStubEmployeeInfoNew'

export default observer(FieldsPayStubEmployeeInfoNew)
